import React, { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../config/api";

/** path string -> blob: URL (shared across all UserAvatar instances) */
const profilePicBlobCache = new Map();
const profilePicInflight = new Map();

function loadProfilePictureBlobUrl(profilePicPath) {
  if (!profilePicPath) return Promise.resolve(null);
  if (profilePicBlobCache.has(profilePicPath)) {
    return Promise.resolve(profilePicBlobCache.get(profilePicPath));
  }
  if (profilePicInflight.has(profilePicPath)) {
    return profilePicInflight.get(profilePicPath);
  }
  const promise = (async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const response = await fetch(`${API_ENDPOINTS.PROFILE_PICS}${profilePicPath}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      profilePicBlobCache.set(profilePicPath, url);
      return url;
    } catch (e) {
      console.warn("UserAvatar: failed to load profile picture", e);
      return null;
    } finally {
      profilePicInflight.delete(profilePicPath);
    }
  })();
  profilePicInflight.set(profilePicPath, promise);
  return promise;
}

function initialsFromUser({ firstName, lastName, displayName }) {
  if (firstName || lastName) {
    return `${firstName?.charAt(0) ?? ""}${lastName?.charAt(0) ?? ""}`.toUpperCase();
  }
  if (displayName && String(displayName).trim()) {
    return String(displayName)
      .trim()
      .split(/\s+/)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 3);
  }
  return "?";
}

/**
 * Data URL for a circular placeholder avatar with initials (no network request).
 * Uses the same percent-encoded SVG shape as the legacy UsersPage helper (reliable in browsers).
 */
export function getDefaultAvatarDataUrl({
  firstName,
  lastName,
  displayName,
  size: sizeIn = 32,
} = {}) {
  const size = Math.max(8, Math.min(512, Math.round(Number(sizeIn) || 32)));
  const initials = initialsFromUser({ firstName, lastName, displayName });
  const fontSize = Math.max(10, Math.round(size * 0.4375));
  const mid = size / 2;
  const textY = Math.round(size * 0.625);
  const safeInitials = encodeURIComponent(initials);
  return (
    "data:image/svg+xml," +
    `%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'%3E` +
    `%3Crect width='${size}' height='${size}' fill='%23e9ecef'/%3E` +
    `%3Ctext x='${mid}' y='${textY}' font-family='Arial' font-size='${fontSize}' fill='%236c757d' text-anchor='middle'%3E${safeInitials}%3C/text%3E%3C/svg%3E`
  );
}

/**
 * Circular avatar: loads `user.profilePic` or `user.profilePicture` (Resident model)
 * or the `profilePic` prop (API path) with auth; caches blob URLs per path.
 * Pass `src` only if you already have a resolved URL and want to bypass the built-in fetch.
 */
export default function UserAvatar({
  src,
  profilePic,
  user,
  firstName,
  lastName,
  displayName,
  size = 32,
  className = "",
  alt = "User avatar",
  style,
  ...imgProps
}) {
  const [fetchedSrc, setFetchedSrc] = useState(null);
  const [imgFailed, setImgFailed] = useState(false);

  const picPath =
    profilePic ?? user?.profilePic ?? user?.profilePicture ?? null;
  const useExternalSrc = Boolean(src);

  useEffect(() => {
    setImgFailed(false);
  }, [src, picPath]);

  useEffect(() => {
    let cancelled = false;
    if (useExternalSrc || !picPath) {
      setFetchedSrc(null);
      return () => {
        cancelled = true;
      };
    }
    loadProfilePictureBlobUrl(picPath).then((url) => {
      if (!cancelled) setFetchedSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [useExternalSrc, picPath]);

  const fn = user?.firstName ?? firstName;
  const ln = user?.lastName ?? lastName;
  const dn =
    displayName ??
    (fn || ln ? undefined : user ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() : undefined);

  const numericSize = Math.max(8, Math.min(512, Math.round(Number(size) || 32)));
  const photoSrc = useExternalSrc ? src : fetchedSrc;
  const hasPhoto = Boolean(photoSrc) && !imgFailed;
  const resolvedSrc = hasPhoto
    ? photoSrc
    : getDefaultAvatarDataUrl({
        firstName: fn,
        lastName: ln,
        displayName: dn,
        size: numericSize,
      });

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      className={className}
      style={{
        width: numericSize,
        height: numericSize,
        minWidth: numericSize,
        minHeight: numericSize,
        flexShrink: 0,
        borderRadius: "50%",
        objectFit: "cover",
        display: "block",
        ...style,
      }}
      onError={() => {
        if (photoSrc) setImgFailed(true);
      }}
      {...imgProps}
    />
  );
}
